class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.setHeight.bind(this));
      // Sticky offsets include padding and the separator, not only the content box.
      this.resizeObserver.observe(this, { box: 'border-box' });
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      // Cache layout computations to reduce reflow
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      // Batch style updates
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get compactOnScroll() {
    return this.dataset.compactOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    this.boundScrollHandler = this.onScrollHandler.bind(this);
    window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
    if (this.compactOnScroll) window.addEventListener('pageshow', this.boundScrollHandler);
    // 11.10, improvement rolig_kategorilinje: a reload mid-page restores the scroll position before this listener
    // exists (Chromium, about half the reloads), so the header stayed «at the top» (no line, no shadow) over the
    // content until the first scroll. Read the position once now. Switch off: as before.
    if (this.compactOnScroll || (document.body && document.body.matches('[data-ll-flags~="rolig_kategorilinje"]'))) this.onScrollHandler();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.boundScrollHandler);
    window.removeEventListener('pageshow', this.boundScrollHandler);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      if (this.compactOnScroll) {
        // Separate thresholds keep the header steady while its own height changes
        // and the browser adjusts scroll anchoring. Restore its full size near the top.
        const compact = headerSection.classList.contains('header-compact');
        headerSection.classList.toggle('header-compact', compact ? scrollTop > 64 : scrollTop > 160);
      }
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (this.hasAttribute('trigger')) {
      return this.getAttribute('trigger');
    } else {
      return 'click';
    }
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      if (this.classList.contains('ll-nav-dropdown')) {
        this.contentElement.setAttribute('open', '');
        // Acquire the new menu's backdrop before releasing the previous menu.
        document.querySelectorAll('.ll-nav-dropdown[open]').forEach(dropdown => {
          if (dropdown !== this && dropdown.open) dropdown.open = false;
        });
      } else {
        setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      }

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    if (this.classList.contains('ll-nav-dropdown')) return this.animateLaserlabMenu(true);
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    if (this.classList.contains('ll-nav-dropdown')) return this.animateLaserlabMenu(false);
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  animateLaserlabMenu(visible) {
    this.laserlabMenuAnimation?.stop();
    if (visible) this.contentElement.style.visibility = 'visible';
    const animation = FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: visible ? 1 : 0 },
      { duration: FoxTheme.config.motionReduced ? 0 : 0.2, easing: 'ease' }
    );
    this.laserlabMenuAnimation = animation;
    return animation.finished.then(() => {
      if (this.laserlabMenuAnimation !== animation) return;
      if (!visible && !this.open) this.contentElement.style.visibility = 'hidden';
    });
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    const nextSummary = event.relatedTarget?.closest?.('.ll-nav-dropdown > summary');
    if (this.classList.contains('ll-nav-dropdown') && nextSummary && nextSummary.parentElement !== this) return;
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.cancelPendingAnimation();
    if (this.open && this.classList.contains('active')) {
      this.onCloseButtonClick({ currentTarget: this.closeButton });
      return;
    }
    this.open = true;
    const activate = () => {
      if (!this.open || !this.closest('menu-drawer')?.open) return;
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    };
    if (FoxTheme.config.motionReduced) activate();
    else {
      // Let the revealed panel acquire its starting position before sliding in.
      this.activationFrame = requestAnimationFrame(() => {
        this.activationFrame = requestAnimationFrame(activate);
      });
    }
  }

  cancelPendingAnimation() {
    cancelAnimationFrame(this.activationFrame);
    clearTimeout(this.closeTimer);
  }

  onCloseButtonClick(event) {
    this.cancelPendingAnimation();
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation(event?.currentTarget === this.closeButton);
  }

  closeAnimation(restoreFocus = false) {
    const finish = () => {
      this.removeAttribute('open');
      if (restoreFocus && this.closest('menu-drawer')?.open) {
        this.summary.focus({ preventScroll: true });
      }
    };
    if (FoxTheme.config.motionReduced || !this.closest('menu-drawer')?.open) finish();
    else this.closeTimer = setTimeout(finish, 200);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get menuItems() {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.menu-drawer__menus:not(.active) > li');
    }
    return this._menuItems;
  }

  prepareToShow() {
    super.prepareToShow();
    this.animateMenuItems();
  }

  animateMenuItems() {
    this.stopMenuItemsAnimation();
    if (FoxTheme.config.motionReduced) return;
    const animation = FoxTheme.Motion.animate(
      this.menuItems,
      {
        opacity: [0, 1],
      },
      {
        duration: 0.2,
        easing: [0.075, 0.82, 0.165, 1],
      }
    );
    this.menuItemsAnimation = animation;
    animation.finished.then(() => {
      if (this.menuItemsAnimation !== animation) return;
      this.menuItemsAnimation = null;
      this.menuItems.forEach(item => item.style.removeProperty('opacity'));
    });
  }

  stopMenuItemsAnimation() {
    this.menuItemsAnimation?.stop();
    this.menuItemsAnimation = null;
    this.menuItems.forEach(item => item.style.removeProperty('opacity'));
  }

  handleShowTransition(animate = true) {
    // Unlike the shared modal timer, this activation can be cancelled by hide().
    return new Promise(resolve => {
      const finish = () => {
        clearTimeout(this.menuActivationTimer);
        this.overlay.removeEventListener('transitionend', finish);
        if (this.finishMenuShow === finish) this.finishMenuShow = null;
        resolve();
      };
      this.finishMenuShow = finish;
      if (FoxTheme.config.motionReduced || !animate) {
        this.setAttribute('active', '');
        finish();
        return;
      }
      this.overlay.addEventListener('transitionend', finish, { once: true });
      this.menuActivationTimer = setTimeout(() => {
        if (this.open) this.setAttribute('active', '');
        else finish();
      }, 75);
    });
  }

  handleHideTransition() {
    this.stopMenuItemsAnimation();
    clearTimeout(this.menuActivationTimer);
    if (this.restoringHistory || FoxTheme.config.motionReduced || !this.hasAttribute('active')) {
      // Complete the native show/hide pair in order so its lock count balances,
      // even when no overlay transition occurs during a cancelled opening.
      this.finishMenuShow?.();
      this.removeAttribute('active');
      return Promise.resolve();
    }
    return super.handleHideTransition();
  }

  resetAfterHistoryNavigation() {
    // A cached page can retain an open drawer. Release its native focus/scroll
    // lock without waiting for a transition that was interrupted by navigation.
    this.restoringHistory = true;
    this.hide();
    this.restoringHistory = false;
  }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      if (this.open) return;
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }
}
customElements.define('menu-product-list', MenuProductList);
